// Shared TanStack Query hook for the active org's billing snapshot.
//
// Centralizes the query key so the trial banner, billing settings page, and
// usage settings page all share a single cache entry. Any one of them
// triggers the fetch, and the others get the result for free.

import { useMemo } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { getOrgBilling, type OrgBillingSnapshot, type SubscriptionStatus } from '@pc/db';
import { useActiveContext } from '../auth/ActiveContextProvider';

export const BILLING_QUERY_KEY = ['operator', 'billing'] as const;
export const MODULE_FLAGS_KEY = 'moduleFlags' as const;

export function useOrgBilling() {
  const { activeOrgId } = useActiveContext();
  return useQuery<OrgBillingSnapshot>({
    queryKey: BILLING_QUERY_KEY,
    queryFn: getOrgBilling,
    enabled: Boolean(activeOrgId),
  });
}

/** Refetch billing snapshot and module nav flags after a subscription change. */
export async function invalidateOperatorBillingCaches(
  qc: QueryClient,
  orgId?: string | null,
): Promise<void> {
  await Promise.all([
    qc.invalidateQueries({ queryKey: BILLING_QUERY_KEY, refetchType: 'active' }),
    qc.invalidateQueries({
      queryKey: orgId ? [MODULE_FLAGS_KEY, orgId] : [MODULE_FLAGS_KEY],
      refetchType: 'active',
    }),
  ]);
}

/**
 * Derived subscription state used by route gates, banners, and the
 * trial-expired upsell. Computed from `subscription.status` and
 * `subscription.trial_end` so the UI gates on the same eligibility window
 * the database uses (billing.fn_sync_org_module_flags v2).
 *
 * Platform-admin override: `subscription.dev_paid_override=true` short-
 * circuits the entire derivation to "fully paid" so internal QA orgs can
 * exercise paid features without putting real money through Stripe. See
 * migration 20260628100000_billing_dev_paid_override.sql.
 */
export interface BillingState {
  /** True while data is still loading. Callers should NOT lock the UI
   * during loading; treat the user as eligible until we know otherwise. */
  loading: boolean;
  /** Raw status from billing.org_subscriptions. */
  status: SubscriptionStatus | null;
  /** trialing AND trial_end > now */
  isTrialActive: boolean;
  /** trialing AND trial_end <= now, OR past_due / canceled / unpaid /
   * incomplete{_expired}. In any of these states the operator app should
   * hard-lock everything but Billing + Settings. */
  isTrialExpired: boolean;
  /** Paid subscription (status='active') OR dev_paid_override=true. */
  isPaid: boolean;
  /** True when the user should be redirected to /settings/billing and
   * blocked from module routes. Combines isTrialExpired with the
   * absence of an active paid plan. Always false when the platform-admin
   * dev_paid_override is on. */
  isLockedOut: boolean;
  /** True when the platform-admin dev_paid_override is treating this org
   * as paid. Surfaced so admin-only UI can render the override banner. */
  isDevPaidOverride: boolean;
}

const LOCKED_STATUSES: ReadonlyArray<SubscriptionStatus> = [
  'past_due',
  'canceled',
  'unpaid',
  'incomplete',
  'incomplete_expired',
];

export function deriveBillingState(
  snapshot: OrgBillingSnapshot | null | undefined,
  loading: boolean,
): BillingState {
  const sub = snapshot?.subscription ?? null;
  const status = (sub?.status ?? null) as SubscriptionStatus | null;
  const trialEndMs = sub?.trial_end ? new Date(sub.trial_end).getTime() : null;
  const trialExpired =
    trialEndMs !== null && !Number.isNaN(trialEndMs) && trialEndMs <= Date.now();
  const overrideOn = sub?.dev_paid_override === true;

  // Platform-admin override short-circuits everything. The DB-side
  // fn_sync_org_module_flags already treats dev_paid_override as
  // paid-eligible; mirror that on the React side so the trial banner,
  // TrialExpiredGate, and any "paid plan" UX agree with the modules
  // panel.
  if (overrideOn) {
    return {
      loading,
      status,
      isTrialActive: false,
      isTrialExpired: false,
      isPaid: true,
      isLockedOut: false,
      isDevPaidOverride: true,
    };
  }

  const isTrialActive = status === 'trialing' && !trialExpired;
  const isPaid = status === 'active';
  const isTrialExpiredState =
    (status === 'trialing' && trialExpired) ||
    (status !== null && LOCKED_STATUSES.includes(status));

  return {
    loading,
    status,
    isTrialActive,
    isTrialExpired: isTrialExpiredState,
    isPaid,
    isLockedOut: isTrialExpiredState && !isPaid,
    isDevPaidOverride: false,
  };
}

/**
 * Convenience hook returning the derived state. Shares the billing
 * snapshot cache with useOrgBilling, so calling both in the same render
 * tree is free.
 */
export function useBillingState(): BillingState {
  const q = useOrgBilling();
  return useMemo(
    () => deriveBillingState(q.data, q.isPending || q.isLoading),
    [q.data, q.isPending, q.isLoading],
  );
}

/**
 * True when the org is in `status='trialing'` AND the trial is set to
 * auto-convert into a paid subscription when it ends. This is the
 * "operator has already picked a plan and committed via Stripe Checkout,
 * the 14-day trial is just a free preview window" state.
 *
 * Detection uses three signals from billing.org_subscriptions:
 *
 *   1. `status === 'trialing'` — actually on a trial.
 *   2. `stripe_subscription_id !== null` — the operator has gone through
 *      Stripe Checkout; a real Stripe subscription exists. Bootstrap-only
 *      trial rows (created by `rpc_bootstrap_org` before any Checkout)
 *      have no Stripe id and DO need an upgrade CTA.
 *   3. `!cancel_at_period_end` — autorenew is on. If the operator
 *      canceled via the customer portal mid-trial, Stripe flips this
 *      to `true` and the subscription will end at trial_end without
 *      converting, so the upgrade CTA is meaningful again.
 *
 * Used by TrialBanner to suppress the "Upgrade now" CTA in cases where
 * there is no workflow to bypass the trial — the subscription will
 * auto-convert when the trial ends.
 */
export function isAutoRenewingTrial(
  snapshot: OrgBillingSnapshot | null | undefined,
): boolean {
  const sub = snapshot?.subscription;
  if (!sub) return false;
  if (sub.status !== 'trialing') return false;
  if (!sub.stripe_subscription_id) return false;
  if (sub.cancel_at_period_end) return false;
  return true;
}

/**
 * Returns the number of whole days remaining on the org's trial, or null if
 * the org is not on a trial. Negative values are clamped to 0 so an expired
 * trial (waiting for the Stripe webhook to flip the status) still renders
 * deterministically.
 */
export function trialDaysRemaining(snapshot: OrgBillingSnapshot | null | undefined): number | null {
  const sub = snapshot?.subscription;
  if (!sub) return null;
  if (sub.status !== 'trialing') return null;
  if (!sub.trial_end) return null;
  const endMs = new Date(sub.trial_end).getTime();
  if (Number.isNaN(endMs)) return null;
  const diffMs = endMs - Date.now();
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / 86_400_000);
}
